/** Schmale Hülle um die REST-API des Backends (/api/...). */
import type { CreateLayoutInput, HouseKey, HouseSummary, Layout, LayoutKind, LayoutSummary, Me, UpdateLayoutInput } from "@hausplaner/model";

const DEV_USER_KEY = "hausplaner-dev-user";

/** Im Entwicklungsmodus (AUTH_MODE=dev) wählt man den Nutzer selbst; der Server liest den Header nur dann. */
export function getDevUser(): string {
  try { return localStorage.getItem(DEV_USER_KEY) || "patrick"; } catch { return "patrick"; }
}
export function setDevUser(name: string) {
  try { localStorage.setItem(DEV_USER_KEY, name); } catch { /* egal */ }
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) { super(message); }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "x-dev-user": getDevUser() };
  if (body !== undefined) headers["content-type"] = "application/json";
  // redirect: "manual" – Umleitungen (z. B. durch einen vorgeschalteten Proxy) nicht stillschweigend folgen
  const res = await fetch("/api" + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: "same-origin", redirect: "manual" });
  if (res.type === "opaqueredirect") throw new ApiError(401, "Sitzung abgelaufen – bitte neu anmelden");
  const text = await res.text();
  let data: unknown = undefined;
  try { data = text ? JSON.parse(text) : undefined; } catch { /* kein JSON */ }
  if (!res.ok) {
    const msg = (data as { message?: string } | undefined)?.message || res.statusText || "Fehler";
    throw new ApiError(res.status, msg, data);
  }
  // Antwort ohne JSON (z. B. statischer Server ohne Backend) gilt als "kein Backend"
  if (data === undefined && res.status !== 204) throw new ApiError(0, "Keine API-Antwort");
  return data as T;
}

export const api = {
  me: () => req<Me>("GET", "/me"),
  login: (username: string, password: string, remember: boolean) => req<{ ok: true }>("POST", "/auth/login", { username, password, remember }),
  logout: () => req<{ ok: true }>("POST", "/auth/logout", {}),
  houses: () => req<HouseSummary[]>("GET", "/houses"),
  listLayouts: (house: HouseKey, kind: LayoutKind) => req<LayoutSummary[]>("GET", `/layouts?house=${house}&kind=${kind}`),
  getLayout: (id: string) => req<Layout>("GET", `/layouts/${id}`),
  createLayout: (input: CreateLayoutInput) => req<Layout>("POST", "/layouts", input),
  updateLayout: (id: string, input: UpdateLayoutInput) => req<Layout>("PUT", `/layouts/${id}`, input),
  duplicateLayout: (id: string, name: string) => req<Layout>("POST", `/layouts/${id}/duplicate`, { name }),
  deleteLayout: (id: string) => req<void>("DELETE", `/layouts/${id}`),
};
